import React, { useContext } from 'react';
import SSPContentVO from 'sspcontentext/src/model/SSPContentVO';
import i18next from 'i18next';
import SSPContentFieldAccessor from 'sspcontentext/src/model/accessor/SSPContentFieldAccessor';
import SspProviderContext from './SspProvider/context.ts';
import { iDataModel, iOverlay } from './interfaces.ts';
import { iSspProviderContext } from './SspProvider/interfaces.ts';
import fetchContentByHash from './fetchContentByHash.ts';
import { getSetup } from './getSetup.ts';
import { useTranslation } from 'react-i18next';

function adapter(
    sspData: SSPContentFieldAccessor,
    store: iSspProviderContext
): iDataModel {
    const headerImg =
        sspData.ref('headerasset').asAsset.fileFamily === 'video'
            ? sspData.ref('headerasset').asAsset.posterUrl
            : sspData.ref('headerasset').asAsset.url;
    const headerVideo =
        sspData.ref('headerasset').asAsset.fileFamily === 'video'
            ? sspData.ref('headerasset').asAsset.url
            : '';
    const modelName =
        (sspData.target.modelname &&
            sspData.target.modelname[i18next.language]) ||
        '';
    const vehicleData = sspData.target.vehicledata[i18next.language] || {};
    const disclaimer =
        sspData.ref('showcarapp_disclaimer').asModel.target.disclaimers[
            i18next.language
        ] || {};

    const mainPageOverlays = (sspData.reflist('showcarapp_popups') || []).map(
        (overlay: SSPContentVO): iOverlay => {
            const overlayModel = overlay.asModel.target;

            return {
                title:
                    (overlayModel.title &&
                        overlayModel.title[i18next.language]) ||
                    overlayModel.title ||
                    '',
                tile_title:
                    (overlayModel.buttontitle &&
                        overlayModel.buttontitle[i18next.language]) ||
                    overlayModel.buttontitle ||
                    '',
                subtitle:
                    (overlayModel.subtitle &&
                        overlayModel.subtitle[i18next.language]) ||
                    overlayModel.subtitle ||
                    '',
                description:
                    (overlayModel.description &&
                        overlayModel.description[i18next.language]) ||
                    overlayModel.description ||
                    '',
                uid: overlay.uid || '',
                price:
                    (overlay.asModel.target.price &&
                        overlay.asModel.target.price[i18next.language]) ||
                    overlay.asModel.target.price ||
                    '',
                price_title:
                    (overlay.asModel.target.pricetitle &&
                        overlay.asModel.target.pricetitle[i18next.language]) ||
                    overlay.asModel.target.pricetitle ||
                    '',
                preview_image:
                    overlay.asModel.ref('headerasset').asAsset.fileFamily ===
                    'video'
                        ? overlay.asModel.ref('headerasset').asAsset.posterUrl
                        : overlay.asModel.ref('headerasset').asAsset.url,
                video:
                    overlay.asModel.ref('headerasset').asAsset.fileFamily ===
                    'video'
                        ? overlay.asModel.ref('headerasset').asAsset.url
                        : '',
                tech_data: {
                    entries: (
                        overlay.asModel
                            .reflist('engine')?.[0]
                            ?.asModel.reflist('entries') || []
                    ).map(entry => ({
                        name: entry.asModel.target.name[i18next.language],
                        entries: entry.asModel.target.entries[i18next.language],
                    })),
                },
            };
        }
    );

    let secondDisplayUrl;
    let secondDisplayType;
    async function getLabelInfo() {
        const labelInfo = await fetchContentByHash(
            sspData.ref('seconddisplay').asModel.target.co2_asset[
                i18next.language
            ]
        );

        const setup = getSetup();
        const url =
            localStorage.getItem('use_qs') === 'true'
                ? setup.market.qsUrl
                : setup.market?.prodUrl;

        secondDisplayUrl = `${url}/module/delivery/bpca/api/v2/assets/${labelInfo.AssetDataSet?.AssetFileProperties?.FileHash}`;
        secondDisplayType =
            labelInfo?.AssetDataSet?.AssetFileProperties?.FileType;
    }

    getLabelInfo();

    const status = sspData.obj('date').Timestamp || '';
    const languages = store.state.content?.languages || [];

    return {
        headerImg,
        headerVideo,
        modelName,
        vehicleData,
        disclaimer,
        mainPageOverlays,
        second_display: {
            url: secondDisplayUrl,
            type: secondDisplayType,
        },
        status,
        languages,
    };
}

function useAdapter(): iDataModel {
    const store = useContext(SspProviderContext);
    const contentModel = store.state.content?.asModel;
    const { i18n } = useTranslation();

    if (contentModel === undefined) {
        throw new Error('contentModel is undefined');
    }
    return React.useMemo(
        () => adapter(contentModel, store),
        [contentModel, store, i18n.language]
    );
}

export default useAdapter;
